import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { RefreshCw, Download, MapPin, Filter, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_STYLES = {
  financial: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  activity: 'bg-blue-50 text-blue-700 border-blue-200',
  security: 'bg-rose-50 text-rose-700 border-rose-200',
};

const EVENT_TYPES = [
  { value: '', label: 'All events' },
  { value: 'wallet_topup', label: 'Wallet Top-up' },
  { value: 'wallet_credit', label: 'Wallet Credit' },
  { value: 'wallet_debit', label: 'Wallet Debit' },
  { value: 'wallet_manual_credit', label: 'Manual Credit (offline)' },
  { value: 'wallet_manual_debit', label: 'Manual Debit (offline)' },
  { value: 'wallet_reconcile', label: 'Reconciliation Fix' },
  { value: 'wallet_topup_failed', label: 'Top-up Failed' },
  { value: 'subscription_purchase', label: 'Subscription Purchase' },
  { value: 'refund_issued', label: 'Refund' },
  { value: 'login', label: 'Login' },
  { value: 'login_failed', label: 'Failed Login' },
  { value: 'tier_change', label: 'Tier Change' },
];

const WALLET_TYPES = [
  { value: '', label: 'All wallets' },
  { value: 'ott', label: 'OTT' },
  { value: 'iptv', label: 'IPTV' },
  { value: 'railtel', label: 'Railtel' },
];

const SOURCES = [
  { value: '', label: 'All sources' },
  { value: 'online', label: 'Online' },
  { value: 'webhook', label: 'S2S Webhook' },
  { value: 'offline', label: 'Offline / Manual' },
  { value: 'system', label: 'System' },
];

const fmtAmount = (n) => n == null ? '—' : `Rs.${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AuditTrail() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    q: '',
    event_type: '',
    category: '',
    wallet_type: '',
    source: '',
    from_date: '',
    to_date: '',
    role: '',
  });
  const [skip, setSkip] = useState(0);
  const LIMIT = 100;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
      p.append('limit', LIMIT);
      p.append('skip', skip);
      const res = await fetch(`${API}/api/admin/audit-log?${p}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Load failed');
        return;
      }
      const data = await res.json();
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error('Load failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [token, filters, skip]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = async () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    try {
      const res = await fetch(`${API}/api/admin/audit-log/export?${p}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error('Export failed');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error('Export failed: ' + e.message);
    }
  };

  const clearFilters = () => {
    setFilters({ q: '', event_type: '', category: '', wallet_type: '', source: '', from_date: '', to_date: '', role: '' });
    setSkip(0);
  };

  const setF = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setSkip(0); };

  return (
    <div className="space-y-4" data-testid="audit-trail-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Audit Trail</h2>
          <p className="text-sm text-slate-500 mt-1">
            Every debit, credit, top-up, refund, login and permission change captured with IP address and geo-location.
            12-month rolling retention.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="reload-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Reload
          </Button>
          <Button onClick={exportCSV} data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="h-4 w-4" /> Filters
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
              <X className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input
              placeholder="Search actor / IP / description…"
              value={filters.q}
              onChange={(e) => setF('q', e.target.value)}
              data-testid="filter-search"
            />
            <Select value={filters.event_type || 'all'} onValueChange={(v) => setF('event_type', v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="filter-event-type"><SelectValue placeholder="Event type" /></SelectTrigger>
              <SelectContent>
                {EVENT_TYPES.map((e) => (
                  <SelectItem key={e.value || 'all'} value={e.value || 'all'}>{e.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.wallet_type || 'all'} onValueChange={(v) => setF('wallet_type', v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="filter-wallet-type"><SelectValue placeholder="Wallet" /></SelectTrigger>
              <SelectContent>
                {WALLET_TYPES.map((w) => (
                  <SelectItem key={w.value || 'all'} value={w.value || 'all'}>{w.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.source || 'all'} onValueChange={(v) => setF('source', v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
              <SelectContent>
                {SOURCES.map((s) => (
                  <SelectItem key={s.value || 'all'} value={s.value || 'all'}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={filters.from_date}
              onChange={(e) => setF('from_date', e.target.value)}
              data-testid="filter-from-date"
              placeholder="From date"
            />
            <Input
              type="date"
              value={filters.to_date}
              onChange={(e) => setF('to_date', e.target.value)}
              data-testid="filter-to-date"
              placeholder="To date"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="pt-4">
          <div className="mb-2 text-sm text-slate-500 flex justify-between">
            <span>Showing {rows.length} of {total.toLocaleString()} rows</span>
            <span>
              <Button variant="ghost" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - LIMIT))}>‹ Prev</Button>
              <Button variant="ghost" size="sm" disabled={skip + LIMIT >= total} onClick={() => setSkip(skip + LIMIT)}>Next ›</Button>
            </span>
          </div>
          <Table data-testid="audit-table">
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Target</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>IP / Location</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-slate-500">
                    No audit rows match your filters.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.audit_id} className="text-xs">
                  <TableCell className="whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={CATEGORY_STYLES[r.category] || ''}>
                      {r.event_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{r.actor_name || r.actor_id}</div>
                    <div className="text-slate-500 font-mono text-[10px]">{r.actor_id}</div>
                    <div className="text-slate-500 font-mono">{r.actor_role}</div>
                  </TableCell>
                  <TableCell>
                    {r.target_customer_id ? (
                      <>
                        <div>{r.target_customer_name || r.target_customer_id}</div>
                        <div className="text-slate-500 font-mono text-[10px]">{r.target_customer_id}</div>
                      </>
                    ) : <span className="text-slate-400">—</span>}
                  </TableCell>
                  <TableCell className={`text-right whitespace-nowrap ${
                    r.event_type.includes('debit') ? 'text-red-700' :
                    r.event_type.includes('credit') || r.event_type.includes('topup') ? 'text-emerald-700' : ''
                  }`}>
                    {fmtAmount(r.amount)}
                    {r.wallet_type && <div className="text-slate-400 text-[10px]">{r.wallet_type.toUpperCase()}</div>}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px]">{r.source || 'online'}</Badge>
                  </TableCell>
                  <TableCell>
                    {r.ip ? (
                      <div>
                        <div className="font-mono">{r.ip}</div>
                        {r.location && (
                          <div className="text-slate-500 flex items-center gap-0.5">
                            <MapPin className="h-3 w-3" />
                            {[r.location.city, r.location.country_code].filter(Boolean).join(', ')}
                          </div>
                        )}
                      </div>
                    ) : <span className="text-slate-400">—</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
